// src/hooks/useRapportMontage.ts — SCE Issues
// Export de la fiche équipement dans la feuille « Montage et Tests » (27/09).
//
// Seul export Excel de l'appli : il remplace l'export de listes du Lot 4
// (retiré des pages Problèmes et Statistiques). La page donne l'équipement,
// ses problèmes et la référence EDE ; le hook télécharge le modèle, le remplit
// (utils/rapportMontage) et le propose au téléchargement.

import { useCallback, useState } from 'react'
import { useSnackbar } from '@/components/ui/Snackbar'
import type { Equipement, Issue } from '@/types/issue.types'
import type { ReferenceEDE } from '@/utils/documentationEDE'
import {
  construireRapport,
  nomFichierRapport,
  remplirModele,
  type GabaritRapport,
} from '@/utils/rapportMontage'
// Adresse du modèle, empreinte comprise : un modèle remplacé n'est jamais
// servi depuis le cache du navigateur.
import modeleElec from '@/assets/modeles/montage-tests-elec.xlsm?url'
import modeleMeca from '@/assets/modeles/montage-tests-meca.xlsm?url'

const MODELES: Record<GabaritRapport, string> = { ELEC: modeleElec, MECA: modeleMeca }

export interface UseRapportMontageReturn {
  exporter: (
    gabarit: GabaritRapport,
    equipement: Equipement,
    issues: readonly Issue[],
    refEDE: ReferenceEDE | undefined,
  ) => Promise<void>
  /** Le modèle en cours de génération, null sinon. */
  enCours: GabaritRapport | null
}

export function useRapportMontage(): UseRapportMontageReturn {
  const { show } = useSnackbar()
  const [enCours, setEnCours] = useState<GabaritRapport | null>(null)

  const exporter = useCallback<UseRapportMontageReturn['exporter']>(
    async (gabarit, equipement, issues, refEDE) => {
      setEnCours(gabarit)
      try {
        const reponse = await fetch(MODELES[gabarit])
        if (!reponse.ok) throw new Error(`modèle ${gabarit} : HTTP ${reponse.status}`)
        const aujourdhui = new Date()
        const contenu = construireRapport(gabarit, equipement, issues, refEDE, aujourdhui)
        const fichier = await remplirModele(await reponse.arrayBuffer(), contenu)
        telecharger(fichier, nomFichierRapport(gabarit, equipement.numeroSerie, aujourdhui))
        const n = contenu.lignes.length
        show(`Feuille ${gabarit} : ${n} problème${n > 1 ? 's' : ''}`, 'success')
      } catch (err) {
        console.error('[useRapportMontage] export échoué :', err)
        show("L'export Excel a échoué. Réessayez.", 'error')
      } finally {
        setEnCours(null)
      }
    },
    [show],
  )

  return { exporter, enCours }
}

function telecharger(fichier: Blob, nom: string): void {
  const url = URL.createObjectURL(fichier)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nom
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  // Révoquer tout de suite peut interrompre le téléchargement (Safari).
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
